import { Fragment, useEffect, useMemo, useRef, useState, type ChangeEvent, type FormEvent } from "react";
import { ExternalLink, FileSpreadsheet, Pencil, Plus, Search, Trash2 } from "lucide-react";
import { APARTMENT_ROOM_OPTIONS, PROPERTY_TYPES } from "../../../../types";
import { formatCurrency } from "../../../../format";
import { MARKET_COMPARABLE_REJECTION_REASONS } from "../../domain/types";
import type {
  MarketAnalysisComparable,
  MarketComparableRejectionReason,
  MarketComparableDraft,
  MarketFunnelSnapshot,
  MarketSubjectSnapshot
} from "../../domain/types";
import type { CandidatePipelineResult } from "../../data/marketDataProvider";
import type { RealityRadarImportPipelineResult } from "../../data/realityRadar/types";
import {
  calculateDataConfidence,
  describeComparableDifferences,
  describeComparableSelection,
  describeComparableUnknowns
} from "../../calculations/similarity";
import { validateSubject } from "../../domain/validation";
import { comparableDisplayIdentity } from "../../domain/listingIdentity";
import { activeMarketComparables, activeOtherConditionAlternatives, activeSameConditionMarket } from "../../domain/comparableLifecycle";
import { effectiveApartmentArea, isCheaperBetterConditionAlternative } from "../../domain/propertyMatching";
import { CityAutocomplete } from "../../location/CityAutocomplete";
import { StreetAutocomplete } from "../../location/StreetAutocomplete";
import { locationAfterCityChange } from "../../location/streetSearch";
import { MarketFieldSection, MarketInput, MarketSelect } from "../MarketFields";
import { MarketFunnelContext } from "../MarketFunnelContext";

const CONDITION_OPTIONS = ["Pôvodný stav", "Čiastočná rekonštrukcia", "Kompletná rekonštrukcia", "Novostavba", "Iné"];
const SIMPLE_STATE_OPTIONS = ["Pôvodná", "Čiastočne menená", "Nová", "Nezistené"];
const RECONSTRUCTION_OPTIONS = ["Bez rekonštrukcie", "Čiastočná", "Kompletná", "Novostavba", "Nezistené"];
const CORE_OPTIONS = ["Umakartové", "Murované", "Iné", "Nezistené"];
const KITCHEN_OPTIONS = ["Pôvodná", "Staršia", "Modernizovaná", "Nová", "Nezistené"];
const BALCONY_OPTIONS = ["Bez balkóna", "Balkón", "Lodžia", "Terasa", "Viac možností"];
const YES_NO_OPTIONS = ["Áno", "Nie", "Nezistené"];
const ORIENTATION_OPTIONS = ["Sever", "Juh", "Východ", "Západ", "Kombinovaná", "Nezistené"];
const BUILDING_OPTIONS = ["Pôvodný stav", "Čiastočne obnovený", "Kompletne obnovený", "Novostavba", "Nezistené"];
const LISTING_STATUS_OPTIONS = ["Aktívna ponuka", "Stiahnutá ponuka", "Predané", "Overená realizácia", "Iné"];

const safeSourceUrl = (value: string) => /^https?:\/\//i.test(value.trim()) ? value.trim() : undefined;

const comparableSources = (comparable: MarketAnalysisComparable) => {
  const sources = comparable.sourceSnapshot?.sources;
  if (!Array.isArray(sources)) return [];
  return sources.flatMap((source) => {
    if (!source || typeof source !== "object") return [];
    const item = source as Record<string, unknown>;
    return typeof item.sourceName === "string" && typeof item.sourceUrl === "string" && safeSourceUrl(item.sourceUrl)
      ? [{ sourceName: item.sourceName, sourceUrl: item.sourceUrl }]
      : [];
  });
};

const comparableAgency = (comparable: MarketAnalysisComparable) => {
  const value = comparable.sourceSnapshot?.agency;
  return typeof value === "string" && value.trim() ? value.trim() : "Neuvedená";
};

const confidenceLabel = { low: "Nízka", medium: "Stredná", high: "Vysoká" } as const;
const invalidReasonLabel: Record<string, string> = {
  missing_municipality: "Chýbajúca obec",
  invalid_municipality: "Neplatná obec",
  missing_property_type: "Chýbajúci typ nehnuteľnosti",
  invalid_property_type: "Neplatný typ nehnuteľnosti",
  missing_rooms: "Chýbajúci počet izieb",
  invalid_rooms: "Neplatný počet izieb",
  missing_area: "Chýbajúca výmera",
  invalid_area: "Neplatná výmera",
  missing_price: "Chýbajúca cena",
  invalid_price: "Neplatná cena",
  invalid_price_per_m2: "Neplatná cena za m²",
  invalid_date: "Neplatný dátum",
  impossible_values: "Nemožné hodnoty",
  parser_failure: "Chyba spracovania riadka",
  other: "Iný technický problém"
};
const rejectionCategoryLabel = {
  ineligible: "CMA neoprávnené",
  invalid: "Technicky neplatné",
  duplicate: "Duplicita",
  ranked_out: "Nezvolené do výberu"
} as const;

const emptyComparable = (subject: MarketSubjectSnapshot): MarketComparableDraft => ({
  origin: "manual",
  marketRole: "primary",
  city: subject.city,
  cityNormalized: subject.cityNormalized,
  citySource: subject.citySource,
  municipalityId: subject.municipalityId,
  locality: subject.locality,
  street: "",
  streetNormalized: "",
  streetSource: undefined,
  streetId: undefined,
  propertyType: subject.propertyType,
  rooms: subject.rooms,
  area: subject.area,
  condition: "",
  reconstruction: "",
  electricity: "",
  coreType: "",
  kitchen: "",
  balconyType: "",
  cellar: "",
  floor: undefined,
  elevator: "",
  orientation: "",
  buildingCondition: "",
  askingPrice: undefined,
  realizedPrice: undefined,
  listingStatus: "Aktívna ponuka",
  sourceName: "",
  sourceUrl: "",
  capturedAt: new Date().toISOString().slice(0, 10),
  sourceSnapshot: {}
});

const comparableDraft = (comparable: MarketAnalysisComparable): MarketComparableDraft => ({
  origin: comparable.origin,
  marketRole: comparable.marketRole,
  marketContextGroup: comparable.marketContextGroup,
  primaryQualificationReasons: comparable.primaryQualificationReasons,
  city: comparable.city,
  cityNormalized: comparable.cityNormalized,
  citySource: comparable.citySource,
  municipalityId: comparable.municipalityId,
  locality: comparable.locality,
  street: comparable.street,
  streetNormalized: comparable.streetNormalized,
  streetSource: comparable.streetSource,
  streetId: comparable.streetId,
  propertyType: comparable.propertyType,
  rooms: comparable.rooms,
  area: comparable.area,
  condition: comparable.condition,
  reconstruction: comparable.reconstruction,
  electricity: comparable.electricity,
  coreType: comparable.coreType,
  kitchen: comparable.kitchen,
  balconyType: comparable.balconyType,
  cellar: comparable.cellar,
  floor: comparable.floor,
  elevator: comparable.elevator,
  orientation: comparable.orientation,
  buildingCondition: comparable.buildingCondition,
  askingPrice: comparable.askingPrice,
  realizedPrice: comparable.realizedPrice,
  listingStatus: comparable.listingStatus,
  sourceName: comparable.sourceName,
  sourceUrl: comparable.sourceUrl,
  capturedAt: comparable.capturedAt.slice(0, 10),
  sourceSnapshot: comparable.sourceSnapshot
});

const MarketContextCandidateCard = ({
  comparable,
  subject,
  otherCondition = false
}: {
  comparable: MarketAnalysisComparable;
  subject: MarketSubjectSnapshot;
  otherCondition?: boolean;
}) => {
  const identity = comparableDisplayIdentity(comparable);
  const area = effectiveApartmentArea(comparable).value;
  const confidence = calculateDataConfidence(subject, comparable);
  const sourceUrl = safeSourceUrl(comparable.sourceUrl);
  const threat = otherCondition && isCheaperBetterConditionAlternative(subject, comparable);
  return (
    <article className="market-competition-card">
      <header>
        <div><h3>{identity.title}</h3><p>{identity.subtitle}</p></div>
        <strong>{comparable.askingPrice ? formatCurrency(comparable.askingPrice) : "–"}</strong>
      </header>
      <div className="market-context-facts">
        <span>{area ? `${area} m²` : "Výmera nezistená"}</span>
        <span>{comparable.condition || "Stav nezistený"}</span>
        <span>{comparable.askingPrice && area ? `${Math.round(comparable.askingPrice / area).toLocaleString("sk-SK")} €/m²` : "€/m² nezistené"}</span>
      </div>
      <div className="market-context-quality">
        <span>Podobnosť <strong>{Math.round(comparable.similarityScore)} %</strong></span>
        <span>Dôvera <strong>{confidenceLabel[confidence.level]} · {confidence.score} %</strong></span>
      </div>
      <p className="market-context-reason">
        <strong>{otherCondition ? "Sekundárna alternatíva" : "Mimo Primary CMA"}:</strong>{" "}
        {comparable.primaryQualificationReasons?.join(" · ") || (otherCondition ? "materiálne odlišný stav nehnuteľnosti" : "nesplnená prísna CMA kvalifikácia")}
      </p>
      {threat ? <p className="market-threat-label">Lacnejšia alternatíva v lepšom stave</p> : null}
      {sourceUrl ? <a href={sourceUrl} target="_blank" rel="noreferrer"><ExternalLink size={13} /> Otvoriť ponuku</a> : null}
    </article>
  );
};

type ComparablesStepProps = {
  subject: MarketSubjectSnapshot;
  comparables: MarketAnalysisComparable[];
  marketFunnelSnapshot?: MarketFunnelSnapshot;
  readOnly: boolean;
  errors: string[];
  onUpsert: (draft: MarketComparableDraft, comparableId?: string) => string[];
  onRemove: (comparableId: string) => void;
  onDecision: (comparableId: string, decision: "included" | "excluded", reason?: MarketComparableRejectionReason) => void;
  onAnalyze: () => Promise<CandidatePipelineResult>;
  onImportRealityRadar: (file: File) => Promise<RealityRadarImportPipelineResult>;
};

export function ComparablesStep({ subject, comparables, marketFunnelSnapshot, readOnly, errors, onUpsert, onRemove, onDecision, onAnalyze, onImportRealityRadar }: ComparablesStepProps) {
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<string>();
  const [draft, setDraft] = useState<MarketComparableDraft>(() => emptyComparable(subject));
  const [formErrors, setFormErrors] = useState<string[]>([]);
  const [analysisState, setAnalysisState] = useState<{ loading: boolean; result?: CandidatePipelineResult }>({ loading: false });
  const [importState, setImportState] = useState<{ loading: boolean; result?: RealityRadarImportPipelineResult; error?: string }>({ loading: false });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const displayedAnalysisResult = importState.result?.pipeline ?? analysisState.result;
  const displayedMarketFunnel = displayedAnalysisResult?.marketFunnel ?? marketFunnelSnapshot;
  const activeComparables = useMemo(() => activeMarketComparables(comparables), [comparables]);
  const sameConditionMarket = useMemo(() => activeSameConditionMarket(comparables), [comparables]);
  const otherConditionAlternatives = useMemo(() => activeOtherConditionAlternatives(comparables), [comparables]);
  const automaticComparables = activeComparables.filter((comparable) => comparable.origin !== "manual");
  const includedAutomaticCount = automaticComparables.filter((comparable) => comparable.decision !== "excluded").length;
  const manualComparables = activeComparables.filter((comparable) => comparable.origin === "manual");
  const displayedComparables = [...automaticComparables, ...manualComparables];
  const autoDiscoveryKeyRef = useRef<string>();
  const subjectDiscoveryKey = useMemo(() => JSON.stringify({
    city: subject.cityNormalized || subject.city,
    municipalityId: subject.municipalityId,
    locality: subject.locality,
    street: subject.streetNormalized || subject.street,
    propertyType: subject.propertyType,
    rooms: subject.rooms,
    area: subject.area,
    condition: subject.condition,
    reconstruction: subject.reconstruction,
    electricity: subject.electricity,
    coreType: subject.coreType,
    kitchen: subject.kitchen,
    balconyType: subject.balconyType,
    cellar: subject.cellar,
    floor: subject.floor,
    elevator: subject.elevator,
    orientation: subject.orientation,
    buildingCondition: subject.buildingCondition
  }), [subject]);

  useEffect(() => {
    if (!editingId && !isAdding) setDraft(emptyComparable(subject));
  }, [editingId, isAdding, subject]);

  const runAnalysis = async () => {
    setAnalysisState({ loading: true });
    const result = await onAnalyze();
    setAnalysisState({ loading: false, result });
  };

  const importRealityRadar = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setImportState({ loading: true });
    try {
      const result = await onImportRealityRadar(file);
      setImportState({ loading: false, result });
      setAnalysisState({ loading: false, result: result.pipeline });
    } catch (error) {
      setImportState({
        loading: false,
        error: error instanceof Error ? error.message : "Reality Radar export sa nepodarilo importovať."
      });
    }
  };

  useEffect(() => {
    if (readOnly || validateSubject(subject).length > 0) return;
    if (autoDiscoveryKeyRef.current === subjectDiscoveryKey) return;
    autoDiscoveryKeyRef.current = subjectDiscoveryKey;
    void runAnalysis();
  }, [readOnly, subjectDiscoveryKey]);

  const update = <Key extends keyof MarketComparableDraft>(key: Key, value: MarketComparableDraft[Key]) =>
    setDraft((current) => ({ ...current, [key]: value }));

  const startEdit = (comparable: MarketAnalysisComparable) => {
    setDraft(comparableDraft(comparable));
    setEditingId(comparable.id);
    setIsAdding(true);
    setFormErrors([]);
  };

  const closeForm = () => {
    setDraft(emptyComparable(subject));
    setEditingId(undefined);
    setIsAdding(false);
    setFormErrors([]);
  };

  const save = (event: FormEvent) => {
    event.preventDefault();
    const validationErrors = onUpsert(draft, editingId);
    if (validationErrors.length > 0) {
      setFormErrors(validationErrors);
      return;
    }
    closeForm();
  };

  return (
    <div className="market-step-content">
      <div className="market-step-intro market-step-heading-row">
        <div>
          <p className="market-kicker">Porovnateľné nehnuteľnosti</p>
          <h2>Čo dnes ukazuje trh?</h2>
          <p>Ponukové a realizované ceny zostávajú samostatné.</p>
        </div>
        {!readOnly && !isAdding ? (
          <button className="market-icon-command" type="button" onClick={() => setIsAdding(true)}>
            <Plus size={18} aria-hidden="true" /> Pridať
          </button>
        ) : null}
      </div>

      {!readOnly ? (
        <div className="market-analyze-command">
          <div className="market-provider-actions">
            <button
              className="market-secondary-button"
              type="button"
              disabled={importState.loading}
              onClick={() => fileInputRef.current?.click()}
            >
              <FileSpreadsheet size={17} aria-hidden="true" />
              {importState.loading ? "Importujem XLSX…" : "Importovať Reality Radar"}
            </button>
            <input
              ref={fileInputRef}
              className="market-file-input"
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={(event) => void importRealityRadar(event)}
              tabIndex={-1}
              aria-hidden="true"
            />
            <button
              className="market-primary-button"
              type="button"
              disabled={analysisState.loading}
              onClick={() => void runAnalysis()}
            >
              <Search size={17} aria-hidden="true" />
              {analysisState.loading ? "Hľadám aktuálne ponuky…" : analysisState.result ? "Obnoviť porovnania" : "Analyzovať trh"}
            </button>
          </div>
          {analysisState.loading ? <p role="status">Hľadám kvalitné porovnateľné ponuky…</p> : null}
          {importState.error ? <div className="market-error" role="alert">{importState.error}</div> : null}
          {importState.result ? (
            <section className="market-import-summary" aria-label="Súhrn Reality Radar importu">
              <header>
                <span>REALITY RADAR</span>
                <strong>{importState.result.fileName}</strong>
                <time dateTime={importState.result.capturedAt}>Snapshot {new Intl.DateTimeFormat("sk-SK", { dateStyle: "short", timeStyle: "short" }).format(new Date(importState.result.capturedAt))}</time>
              </header>
              <div className="market-import-key-metrics">
                <span>Načítané<strong>{importState.result.rowsLoaded}</strong></span>
                <span>Unikátne<strong>{importState.result.uniqueListings}</strong></span>
                <span>Relevantné<strong>{importState.result.relevantCandidates}</strong></span>
                <span>Priame CMA<strong>{importState.result.topCandidates}</strong></span>
                <span>Rovnaký stav mimo CMA<strong>{importState.result.pipeline.sameConditionMarket.length}</strong></span>
                <span>Iný stav<strong>{importState.result.pipeline.otherConditionAlternatives.length}</strong></span>
              </div>
              <details className="market-import-audit">
                <summary>Diagnostika importu</summary>
                <h4>Spracovanie dát</h4>
                <ul>
                  <li><span>Porovnávaná plocha</span><strong>{importState.result.pipeline.subjectAreaBasis.value ?? "–"} m² · {importState.result.pipeline.subjectAreaBasis.confidence === "low" ? "nižšia dôvera" : "potvrdená"}</strong></li>
                  <li><span>Technicky neplatné riadky</span><strong>{importState.result.invalidRows}</strong></li>
                  <li><span>Odstránené duplicitné riadky</span><strong>{importState.result.duplicateRowsRemoved}</strong></li>
                  <li><span>CMA neoprávnené</span><strong>{importState.result.ineligibleCandidates}</strong></li>
                  <li><span>Nezvolené nad limit</span><strong>{importState.result.rankedOutCandidates}</strong></li>
                  <li><span>Načítané = technicky neplatné + normalizované</span><strong>{importState.result.rowsLoaded} = {importState.result.invalidRows} + {importState.result.normalizedListings}</strong></li>
                  <li><span>Normalizované = odstránené duplicitné riadky + unikátne vstupy CMA</span><strong>{importState.result.normalizedListings} = {importState.result.duplicateRowsRemoved} + {importState.result.uniqueListings}</strong></li>
                  <li><span>Unikátne CMA = neplatné + neoprávnené + duplicitné + kvalifikované</span><strong>{importState.result.uniqueListings} = {importState.result.invalidCandidates} + {importState.result.ineligibleCandidates} + {importState.result.pipeline.duplicateCandidateCount} + {importState.result.relevantCandidates}</strong></li>
                  <li><span>Kvalifikované = vybrané + nezvolené nad limit</span><strong>{importState.result.relevantCandidates} = {importState.result.topCandidates} + {importState.result.rankedOutCandidates}</strong></li>
                </ul>
                {Object.keys(importState.result.invalidPrimaryReasonCounts).length > 0 ? (
                  <div className="market-import-audit-group">
                  <h4>Technicky neplatné riadky ({importState.result.invalidRows})</h4>
                  <ul>
                    {Object.entries(importState.result.invalidPrimaryReasonCounts).map(([reason, count]) => {
                      const samples = importState.result?.invalidSamplesByReason[reason as keyof typeof importState.result.invalidSamplesByReason] ?? [];
                      return (
                        <li key={reason}>
                          <span>
                            {invalidReasonLabel[reason] ?? reason}
                            {samples.length > 0 ? <small>{samples.map((sample) =>
                              `Riadok ${sample.rowNumber}: ${sample.municipality || "bez obce"} · ${sample.propertyType || "bez typu"} · ${sample.rooms || "bez izieb"} · ${sample.area || "bez plochy"} · ${sample.price || "bez ceny"}`
                            ).join(" | ")}</small> : null}
                          </span>
                          <strong>{count}</strong>
                        </li>
                      );
                    })}
                  </ul>
                  </div>
                ) : null}
                {Object.keys(importState.result.pipeline.rejectionPrimaryReasonCounts).length > 0 ? (
                  <div className="market-import-audit-group">
                  <h4>CMA filtre ({importState.result.excludedCandidates})</h4>
                  <ul>
                    {Object.entries(importState.result.pipeline.rejectionPrimaryReasonCounts).map(([reason, count]) => {
                      const samples = importState.result?.pipeline.rejectionSamplesByReason[reason] ?? [];
                      return (
                        <li key={reason}>
                          <span>
                            {reason}
                            {samples.length > 0 ? <small>{samples.map((sample) =>
                              `${sample.city || "bez obce"} · ${sample.propertyType || "bez typu"} · ${sample.rooms || "bez izieb"} · ${sample.area ?? "–"} m² · ${formatCurrency(sample.askingPrice ?? 0)}`
                            ).join(" | ")}</small> : null}
                          </span>
                          <strong>{count}</strong>
                        </li>
                      );
                    })}
                  </ul>
                  </div>
                ) : null}
                {importState.result.pipeline.qualificationAudit.length > 0 ? (
                  <div className="market-import-audit-group">
                  <h4>Kvalifikácia ({importState.result.pipeline.qualificationAudit.length})</h4>
                  <ul>
                    {importState.result.pipeline.qualificationAudit.map((item) => (
                      <li key={item.id}>
                        <span>
                          {item.title || item.locality || item.city} · {item.area ?? "–"} m² · {item.condition || "stav nezistený"} · {item.askingPrice ? formatCurrency(item.askingPrice) : "cena nezistená"}
                          <small>
                            similarity {item.similarityScore.toFixed(1)} · dôvera {item.dataConfidence.toFixed(1)} · selection {item.selectionScore.toFixed(1)} · area {((item.areaScore ?? 0) * 100).toFixed(0)} % · {item.destinationReason}
                          </small>
                        </span>
                        <strong>{item.destination === "primary" ? "Primary" : item.destination === "same_condition" ? "Rovnaký stav" : item.destination === "other_condition" ? "Iný stav" : "Mimo"}</strong>
                      </li>
                    ))}
                  </ul>
                  </div>
                ) : null}
              </details>
            </section>
          ) : null}
          {displayedAnalysisResult ? <p role="status">{displayedAnalysisResult.message}</p> : null}
          {displayedAnalysisResult?.lowDataWarning ? (
            <div className="market-fallback-note" role="status">
              <strong>Málo kvalitných porovnaní</strong>
              <span>Výsledok má nižšiu istotu. Slabé ponuky neboli doplnené iba kvôli počtu.</span>
            </div>
          ) : null}
          {displayedAnalysisResult?.sourceStatuses.length && !importState.result ? (
            <details className="market-discovery-report">
              <summary>Diagnostika zdroja</summary>
              <div>
                <span>Nájdené<strong>{displayedAnalysisResult.rawListingCount}</strong></span>
                <span>Po filtrovaní<strong>{displayedAnalysisResult.preliminaryCandidateCount}</strong></span>
                <span>Unikátne<strong>{displayedAnalysisResult.uniqueListingCount}</strong></span>
                <span>Detailne<strong>{displayedAnalysisResult.detailListingCount}</strong></span>
                <span>Vybrané<strong>{displayedAnalysisResult.candidates.length}</strong></span>
              </div>
              <ul>
                {displayedAnalysisResult.sourceStatuses.map((source) => (
                  <li key={source.providerId}>
                    <span>{source.sourceName}{source.message ? <small>{source.message}</small> : null}</span>
                    <strong>{source.status === "ready" ? `Dostupné · ${source.rawCount}` : source.status === "failed" ? "Chyba zdroja" : "Nedostupné"}</strong>
                  </li>
                ))}
              </ul>
            </details>
          ) : null}
          {displayedAnalysisResult?.fallbackSelections.length ? (
            <div className="market-fallback-note">
              <strong>Kontrolovaný fallback</strong>
              <span>
                {displayedAnalysisResult.fallbackSelections.map((selection) =>
                  `${selection.candidate.locality || selection.candidate.city}: ${selection.reasons.join(", ")}`
                ).join(" · ")}
              </span>
            </div>
          ) : null}
          {displayedAnalysisResult?.rejectedCandidates.length ? (
            <details className="market-rejected-candidates">
              <summary>Vyradené kandidáty ({displayedAnalysisResult.rejectedCandidates.length})</summary>
              <ul>
                {displayedAnalysisResult.rejectedCandidates.map((rejection, index) => {
                  const identity = comparableDisplayIdentity(rejection.candidate);
                  return (
                  <li key={`${rejection.candidate.providerId}-${rejection.candidate.providerPropertyId ?? index}`}>
                    <strong>{identity.title}</strong>
                    <span>{rejectionCategoryLabel[rejection.category]} · {rejection.reasons.join(" · ")}</span>
                  </li>
                  );
                })}
              </ul>
            </details>
          ) : null}
        </div>
      ) : null}

      <MarketFunnelContext snapshot={displayedMarketFunnel} />

      {errors.length > 0 ? <div className="market-error" role="alert">{errors.join(" ")}</div> : null}

      {!readOnly && isAdding ? (
        <form className="market-comparable-form" onSubmit={save}>
          <div className="market-form-title">
            <div><span>{editingId ? "Úprava comparable" : "Nová comparable"}</span><strong>{draft.locality || "Bez lokality"}</strong></div>
            <button type="button" onClick={closeForm}>Zrušiť</button>
          </div>
          {formErrors.length > 0 ? <div className="market-error" role="alert">{formErrors.join(" ")}</div> : null}

          <MarketFieldSection title="Nehnuteľnosť">
            <CityAutocomplete
              value={draft.city}
              citySource={draft.citySource}
              onChange={(selection) => setDraft((current) => locationAfterCityChange(current, selection))}
            />
            <StreetAutocomplete
              value={draft.street}
              municipalityId={draft.municipalityId}
              streetId={draft.streetId}
              streetSource={draft.streetSource}
              onChange={(selection) => setDraft((current) => ({ ...current, ...selection }))}
            />
            <MarketInput label="Lokalita / mikrolokalita" value={draft.locality} onChange={(value) => update("locality", value)} />
            <MarketSelect label="Typ" value={draft.propertyType} options={PROPERTY_TYPES} onChange={(value) => update("propertyType", value)} />
            <MarketSelect label="Počet izieb" value={draft.rooms} options={APARTMENT_ROOM_OPTIONS} onChange={(value) => update("rooms", value)} />
            <MarketInput label="Výmera" type="number" min="1" inputMode="decimal" value={draft.area} suffix="m²" onChange={(value) => update("area", Number(value) || undefined)} />
          </MarketFieldSection>

          <MarketFieldSection title="Cena a stav ponuky">
            <MarketInput label="Ponuková cena" type="number" min="0" inputMode="numeric" value={draft.askingPrice} suffix="€" onChange={(value) => update("askingPrice", Number(value) || undefined)} />
            <MarketInput label="Realizovaná cena" type="number" min="0" inputMode="numeric" value={draft.realizedPrice} suffix="€" onChange={(value) => update("realizedPrice", Number(value) || undefined)} />
            <MarketSelect label="Status" value={draft.listingStatus} options={LISTING_STATUS_OPTIONS} onChange={(value) => update("listingStatus", value)} />
            <MarketInput label="Dátum zachytenia" type="date" value={draft.capturedAt} onChange={(value) => update("capturedAt", value)} />
          </MarketFieldSection>

          <MarketFieldSection title="Technické porovnanie">
            <MarketSelect label="Stav" value={draft.condition} options={CONDITION_OPTIONS} onChange={(value) => update("condition", value)} />
            <MarketSelect label="Rekonštrukcia" value={draft.reconstruction} options={RECONSTRUCTION_OPTIONS} onChange={(value) => update("reconstruction", value)} />
            <MarketSelect label="Elektroinštalácia" value={draft.electricity} options={SIMPLE_STATE_OPTIONS} onChange={(value) => update("electricity", value)} />
            <MarketSelect label="Bytové jadro" value={draft.coreType} options={CORE_OPTIONS} onChange={(value) => update("coreType", value)} />
            <MarketSelect label="Kuchyňa" value={draft.kitchen} options={KITCHEN_OPTIONS} onChange={(value) => update("kitchen", value)} />
            <MarketSelect label="Stav bytového domu" value={draft.buildingCondition} options={BUILDING_OPTIONS} onChange={(value) => update("buildingCondition", value)} />
            <MarketSelect label="Balkón / lodžia" value={draft.balconyType} options={BALCONY_OPTIONS} onChange={(value) => update("balconyType", value)} />
            <MarketSelect label="Pivnica" value={draft.cellar} options={YES_NO_OPTIONS} onChange={(value) => update("cellar", value)} />
            <MarketInput label="Poschodie" type="number" inputMode="numeric" value={draft.floor} onChange={(value) => update("floor", value === "" ? undefined : Number(value))} />
            <MarketSelect label="Výťah" value={draft.elevator} options={YES_NO_OPTIONS} onChange={(value) => update("elevator", value)} />
            <MarketSelect label="Orientácia" value={draft.orientation} options={ORIENTATION_OPTIONS} onChange={(value) => update("orientation", value)} />
          </MarketFieldSection>

          <MarketFieldSection title="Zdroj">
            <MarketInput label="Názov zdroja" value={draft.sourceName} onChange={(value) => update("sourceName", value)} placeholder="Napr. Reality.sk" />
            <MarketInput label="URL zdroja" type="url" value={draft.sourceUrl} onChange={(value) => update("sourceUrl", value)} placeholder="https://" />
          </MarketFieldSection>

          <button className="market-primary-button" type="submit">{editingId ? "Uložiť zmeny" : "Pridať porovnanie"}</button>
        </form>
      ) : null}

      <div className="market-comparable-list">
        {displayedAnalysisResult || displayedComparables.length > 0 ? (
          <h3 className="market-comparable-section-title">PRIAME CMA POROVNANIA: {includedAutomaticCount}</h3>
        ) : null}
        {displayedComparables.length === 0 ? (
          <div className="market-compact-empty">
            {analysisState.loading && !importState.result
              ? "Hľadám dostupné porovnania v meste…"
              : displayedAnalysisResult?.status === "provider-unavailable" && !importState.result
                ? "Automatické porovnania nie sú dostupné – chýba live market provider."
                : "Zatiaľ bez porovnateľných nehnuteľností. Manuálne porovnanie môžeš pridať vyššie."}
          </div>
        ) : displayedComparables.map((comparable, index) => {
          const identity = comparableDisplayIdentity(comparable);
          const sources = comparableSources(comparable);
          const dataConfidence = calculateDataConfidence(subject, comparable);
          const differences = describeComparableDifferences(subject, comparable);
          const unknowns = describeComparableUnknowns(subject, comparable);
          const comparableArea = effectiveApartmentArea(comparable).value;
          const sectionTitle = index === 0
            ? automaticComparables.length > 0 ? undefined : "MANUÁLNE PRIDANÉ"
            : index === automaticComparables.length && manualComparables.length > 0
              ? "MANUÁLNE PRIDANÉ"
              : undefined;
          return (
          <Fragment key={comparable.id}>
          {sectionTitle ? <h3 className="market-comparable-section-title">{sectionTitle}</h3> : null}
          <article className={`market-comparable-card ${comparable.decision === "excluded" ? "excluded" : ""}`}>
            <header>
              <span className="market-rank">{String(index + 1).padStart(2, "0")}</span>
              <div><h3>{identity.title}</h3><p>{identity.subtitle}</p></div>
              <strong>{Math.round(comparable.similarityScore)} %</strong>
            </header>
            <div className="market-comparable-meta market-comparable-sales-facts">
              <span><small>Výmera</small>{comparableArea ? `${comparableArea} m²` : "Nezistená"}</span>
              <span><small>Stav</small>{comparable.condition || "Nezistený"}</span>
              <span><small>Ponuková cena</small>{comparable.askingPrice ? formatCurrency(comparable.askingPrice) : "Nezistená"}</span>
              <span><small>Cena za m²</small>{comparable.askingPrice && comparableArea ? `${Math.round(comparable.askingPrice / comparableArea).toLocaleString("sk-SK")} €/m²` : "Nezistená"}</span>
            </div>
            <div className={`market-data-confidence ${dataConfidence.level}`}>
              <span>Dôvera</span>
              <strong>{confidenceLabel[dataConfidence.level]} · {dataConfidence.score} %</strong>
            </div>
            <details className="market-comparable-details">
              <summary>Podrobnosti porovnania</summary>
              <div className="market-comparable-reasons">
                <div>
                  <span>Prečo bolo vybrané</span>
                  <ul className="market-match-reasons">
                    {describeComparableSelection(comparable).map((explanation) => <li key={explanation}>{explanation}</li>)}
                  </ul>
                </div>
                <div>
                  <span>Rozdiely</span>
                  <p>{differences.join(" · ") || "Žiadny potvrdený rozdiel"}</p>
                </div>
                <div className="market-unknown-data">
                  <span>Neznáme údaje</span>
                  <p>{unknowns.join(" · ") || "Žiadne z kľúčových údajov"}</p>
                </div>
                <div>
                  <span>Zdroj</span>
                  <p>{comparableAgency(comparable)} · {sources.map((source) => source.sourceName).join(" + ") || comparable.sourceName || "Vlastný záznam"}</p>
                </div>
              </div>
              <footer>
              <time dateTime={comparable.capturedAt}>Zachytené {new Intl.DateTimeFormat("sk-SK").format(new Date(comparable.capturedAt))}</time>
              {sources.length > 0 ? (
                <span className="market-source-links">
                  {sources.map((source) => (
                    <a key={source.sourceUrl} href={source.sourceUrl} target="_blank" rel="noreferrer"><ExternalLink size={13} /> Otvoriť · {source.sourceName}</a>
                  ))}
                </span>
              ) : safeSourceUrl(comparable.sourceUrl) ? <a href={safeSourceUrl(comparable.sourceUrl)} target="_blank" rel="noreferrer"><ExternalLink size={14} /> Zdroj</a> : <span>{comparable.sourceName || "Vlastný záznam"}</span>}
              {!readOnly ? (
                <div className="market-card-actions">
                  <button type="button" aria-label="Upraviť comparable" onClick={() => startEdit(comparable)}><Pencil size={15} /></button>
                  <button type="button" aria-label="Odstrániť comparable" onClick={() => onRemove(comparable.id)}><Trash2 size={15} /></button>
                  </div>
                ) : null}
              </footer>
            </details>
            {!readOnly ? (
              <div className="market-decision-row">
                <button className={comparable.decision !== "excluded" ? "active" : ""} type="button" onClick={() => onDecision(comparable.id, "included")}>Použiť</button>
                <button className={comparable.decision === "excluded" ? "active" : ""} type="button" onClick={() => onDecision(comparable.id, "excluded", comparable.rejectionReason ?? "iné")}>Vyradiť</button>
                {comparable.decision === "excluded" ? (
                  <select
                    aria-label="Dôvod vyradenia"
                    value={comparable.rejectionReason ?? "iné"}
                    onChange={(event) => onDecision(comparable.id, "excluded", event.target.value as MarketComparableRejectionReason)}
                  >
                    {MARKET_COMPARABLE_REJECTION_REASONS.map((reason) => <option key={reason} value={reason}>{reason}</option>)}
                  </select>
                ) : null}
              </div>
            ) : comparable.decision === "excluded" ? <p className="market-excluded-note">Vyradené: {comparable.rejectionReason ?? "iné"}</p> : null}
          </article>
          </Fragment>
          );
        })}
      </div>

      {displayedAnalysisResult || sameConditionMarket.length > 0 ? (
        <details className="market-context-section market-competition-section">
          <summary>
            <span>PODOBNÉ PONUKY V ROVNAKOM STAVE: {displayedMarketFunnel?.stages.similarCondition.count ?? includedAutomaticCount + sameConditionMarket.length}</span>
            <small>{sameConditionMarket.length} ďalších mimo Primary CMA · bez vplyvu na ocenenie</small>
          </summary>
          <div className="market-context-list">
            {sameConditionMarket.length === 0
              ? <div className="market-compact-empty">Všetky ponuky v rovnakom stave sú už v Primary CMA.</div>
              : sameConditionMarket.map((comparable) => <MarketContextCandidateCard key={comparable.id} comparable={comparable} subject={subject} />)}
          </div>
        </details>
      ) : null}

      {displayedAnalysisResult || otherConditionAlternatives.length > 0 ? (
        <details className="market-context-section market-competition-section">
          <summary>
            <span>ALTERNATÍVY V INOM STAVE: {otherConditionAlternatives.length}</span>
            <small>sekundárny pohľad kupujúceho · bez vplyvu na ocenenie</small>
          </summary>
          <div className="market-context-list">
            {otherConditionAlternatives.length === 0
              ? <div className="market-compact-empty">Bez relevantných alternatív v inom stave.</div>
              : otherConditionAlternatives.map((comparable) => <MarketContextCandidateCard key={comparable.id} comparable={comparable} subject={subject} otherCondition />)}
          </div>
        </details>
      ) : null}
    </div>
  );
}
